import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerRequestSchema } from '@firegame/shared';
import { ApiClientError } from '../api/client.js';
import { useAuth } from '../store/AuthContext.js';
import { AuthCard } from '../ui/AuthCard.js';
import { Button } from '../ui/Button.js';
import { Field } from '../ui/Field.js';
import { t } from '../i18n/index.js';

export function RegisterPage() {
  const labels = t();
  const auth = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (password !== passwordConfirm) {
      setFieldErrors({ passwordConfirm: labels.auth.register.passwordMismatch });
      return;
    }

    // Validation locale avec le meme schema que le serveur : l'utilisateur a un
    // retour immediat, sans aller-retour reseau inutile.
    const parsed = registerRequestSchema.safeParse({ email, password, displayName });
    if (!parsed.success) {
      setFieldErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [issue.path.map(String).join('.'), issue.message]),
        ),
      );
      return;
    }

    setBusy(true);
    try {
      await auth.register(parsed.data);
      void navigate('/', { replace: true });
    } catch (cause) {
      if (cause instanceof ApiClientError) {
        setError(cause.message);
        setFieldErrors(
          Object.fromEntries(cause.issues.map((issue) => [issue.path, issue.message])),
        );
      } else {
        setError(labels.errors.unexpected);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title={labels.auth.register.title}
      subtitle={labels.auth.register.subtitle}
      footer={
        <>
          {labels.auth.register.haveAccount}{' '}
          <Link to="/connexion">{labels.auth.register.login}</Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="fg-alert fg-alert--error" role="alert">
            {error}
          </div>
        )}

        <Field
          label={labels.auth.displayNameLabel}
          name="displayName"
          autoComplete="nickname"
          required
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          error={fieldErrors.displayName}
          hint={labels.auth.displayNameHint}
          disabled={busy}
        />

        <Field
          label={labels.auth.emailLabel}
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors.email}
          disabled={busy}
        />

        <Field
          label={labels.auth.passwordLabel}
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password}
          hint={labels.auth.passwordHint}
          disabled={busy}
        />

        <Field
          label={labels.auth.passwordConfirmLabel}
          type="password"
          name="passwordConfirm"
          autoComplete="new-password"
          required
          value={passwordConfirm}
          onChange={(event) => setPasswordConfirm(event.target.value)}
          error={fieldErrors.passwordConfirm}
          disabled={busy}
        />

        <div className="fg-auth__actions">
          <Button type="submit" busy={busy}>
            {labels.auth.register.submit}
          </Button>
        </div>
      </form>
    </AuthCard>
  );
}
